/* @jsx h */
import {
  defineComponent,
  ref,
  onMounted,
  onBeforeUnmount,
  h,
  watch,
  computed,
} from "vue";
import { api } from "@app/data/api";
import {
  CartAPI,
  calculateCartItemTotal,
  formatPrice,
  sortTables,
} from "@lemmonhq/orders";
import { Cart, FoodType, Product, TableStatus } from "@lemmonhq/types";
import { createSeatPersonas } from "@app/utils/clientBusiness";
import { Loading, LemmonModal } from "@app/vue/components/index";
import { useAuth, useShop, useToast } from "../../providers";
import { CoursesFinalizer } from "./CoursesFinalizer";
import { TableOrdersSummary } from "./TableOrdersSummary";
import { TableOrders } from "./TableOrders";
import {
  FastQuantitySelector,
  FullScreenSheet,
  useLocalization,
} from "@lemmonhq/uikit";
import { nanoid } from "nanoid";
import { RestaurantSettingKey } from "@lemmonhq/types";
import { getBooleanSetting } from "@app/data/helpers/settings_helpers";
import {
  TableCreditBar,
  TableCreditSheet,
  TableCreditTopUp,
  giftCardItemsOf,
  parseTableCreditError,
  tableCreditAvailable,
  tableCreditItemsOf,
  tableCreditOf,
} from "@app/vue/components/TableCredit";
//
export type TableSelectModalProps = {
  isOpen: boolean;
  onClose: Function;
  onSuccess: Function;
  selectedTable?: TableStatus;
  cart: Cart;
  cartAPI: CartAPI;
  foodTypes: FoodType[];
  products: Product[];
};

type TableSelectModalInnerProps = TableSelectModalProps & {
  showCancel: boolean;
};

export const TableSelectModalInner =
  defineComponent<TableSelectModalInnerProps>({
    name: "TableSelectModalInner",
    props: [
      "isOpen",
      "onClose",
      "onSuccess",
      "cart",
      "cartAPI",
      "selectedTable",
      "foodTypes",
      "products",
      "showCancel",
    ],
    setup(props) {
      const { t } = useLocalization();
      const tables = ref<TableStatus[]>([]);
      const loading = ref(true);
      const creating = ref(false);
      const selectedTableId = ref<number | null>(
        props.selectedTable ? props.selectedTable?.id : null,
      );
      const seatCount = ref<number | null>(
        props.cart.customers.length > 0 ? props.cart.customers.length : null,
      );
      const shop = useShop();
      const toast = useToast();
      const triggeredFoodTypeIds = ref([]);
      const auth = useAuth();
      // The timeline card the cashier tapped, shown in its own modal.
      const openOrder = ref<{ id: number; name: string } | null>(null);
      const showCreditTopUp = ref(false);
      const showCreditHistory = ref(false);
      const currency = computed(
        () => auth.user.value?.restaurant.currency || "CHF",
      );

      // Resolved from the already-fetched table list so the summary needs no
      // extra request. Falls back to the prop while `tables` is still loading.
      const selectedTableStatus = computed(() => {
        if (!selectedTableId.value) return undefined;
        return (
          tables.value.find((table) => table.id === selectedTableId.value) ??
          (props.selectedTable?.id === selectedTableId.value
            ? props.selectedTable
            : undefined)
        );
      });

      // A credited table's orders go on its bill; the credit pays it at
      // checkout. The cashier sees what is still available: the credit minus
      // what the table already owes (docs/plans/table_credit).
      const selectedCredit = computed(() => {
        const credit = tableCreditOf(selectedTableStatus.value);
        return credit?.credited ? credit : null;
      });
      const cartTotal = computed(() =>
        props.cart.items.reduce(
          (sum, item) => sum + calculateCartItemTotal(item),
          0,
        ),
      );
      const creditShort = computed(() =>
        selectedCredit.value
          ? Math.max(
              cartTotal.value - tableCreditAvailable(selectedCredit.value),
              0,
            )
          : 0,
      );
      const beyondCredit = computed(() => creditShort.value > 0.005);
      // Restaurant setting: let the table order beyond its credit (the rest is
      // paid at checkout), or block it until the table tops up. Checked here
      // only; the server does not refuse such an order.
      const allowBeyondCredit = computed(() =>
        getBooleanSetting(
          auth.user.value?.restaurant_settings || [],
          RestaurantSettingKey.ALLOW_ORDERS_EXCEEDING_TABLE_CREDIT,
          false,
        ),
      );
      const blockedByCredit = computed(
        () =>
          !!selectedCredit.value &&
          beyondCredit.value &&
          !allowBeyondCredit.value,
      );
      // Table credit and gift cards are sold with "Pay", never sent to a
      // table; the server refuses them too.
      const cartSellsCredit = computed(
        () => tableCreditItemsOf(props.cart.items, props.products).length > 0,
      );
      const cartSellsGiftCard = computed(
        () => giftCardItemsOf(props.cart.items, props.products).length > 0,
      );

      watch(
        () => props.isOpen,
        (isOpen) => {
          if (!isOpen) return;
          openOrder.value = null;
          selectedTableId.value = props.selectedTable
            ? props.selectedTable.id
            : null;
          seatCount.value =
            props.cart.customers.length > 0
              ? props.cart.customers.length
              : null;
        },
        { immediate: true },
      );

      const handleSeatCountChange = (value: number | null) => {
        seatCount.value = value;

        const { customers, firstCustomerId } = createSeatPersonas({
          count: value ?? 0,
          createId: () => nanoid(),
        });

        props.cartAPI.updateCart({
          ...props.cart,
          customers,
        });
        //props.cartAPI.activeCustomerId.value = firstCustomerId;
      };

      onMounted(() => {
        const featureFlags = auth.user.value?.restaurant.feature_flags || {};
        if (featureFlags["la_suite_default_on"]) {
          props.cartAPI.showFoodTypes.value = true;
        }
      });

      const loadTables = async () => {
        try {
          const data = await api.getTablesMeta();
          tables.value = data;
        } catch (error) {
          console.error("Failed to fetch tables:", error);
        } finally {
          loading.value = false;
        }
      };
      onMounted(loadTables);

      const handleSubmit = async (e?: Event) => {
        e?.preventDefault();
        console.log("selectedTableId", selectedTableId.value);
        if (!selectedTableId.value) return;
        const tableStatus =
          props.selectedTable?.id === selectedTableId.value
            ? props.selectedTable
            : tables.value.find((table) => table.id === selectedTableId.value);

        creating.value = true;
        try {
          await api.createOrder(
            {
              payload: {
                type: "cart",
                object: {
                  ...props.cart,
                  tableId: selectedTableId.value,
                  reservationId: tableStatus?.party?.reservation_id ?? null,
                  // La Suite is hidden on a credited table.
                  isMultiCourse:
                    !selectedCredit.value && props.cartAPI.showFoodTypes.value,
                  triggeredFoodTypeIds: triggeredFoodTypeIds.value,
                },
              },
              serviceMethod: "dine-in",
            },
            shop,
          );
          shop.refreshOrders();
          shop.refreshTables();
          props.onSuccess();
        } catch (e: any) {
          console.error(e);
          const creditError = parseTableCreditError(e?.message);
          toast.showToast(
            creditError?.code === "product_on_table"
              ? t("pos.table_credit.product_on_table")
              : e?.message?.includes("gift_card_product_on_table")
                ? t("pos.gift_card.product_on_table")
                : e.message || "Failed placing order",
            { type: "error" },
          );
        } finally {
          creating.value = false;
        }
      };
      console.log("TableSelectModal", props);

      return () => (
        <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div class="flex min-h-0 flex-1 overflow-hidden">
            <div class="w-full flex flex-col flex-1 overflow-hidden justify-center items-center h-full">
              {loading.value ? (
                <div class="py-1 px-4 xl:px-3 flex flex-col justify-center min-h-24 flex-1">
                  <Loading />
                </div>
              ) : (
                <div class="min-h-0 w-full flex flex-col flex-1 overflow-hidden">
                  <div class="min-h-0 flex flex-col flex-1 overflow-hidden gap-2 p-2 lg:p-3 xl:px-6">
                    <div class="flex flex-row items-center gap-2">
                      <select
                        class="select select-bordered w-full bg-[#2a3138]  flex-1"
                        value={selectedTableId.value || ""}
                        onChange={(e) => {
                          const val = (e.target as HTMLSelectElement).value;
                          selectedTableId.value = val ? Number(val) : null;
                          openOrder.value = null;
                        }}
                      >
                        <option value="">
                          {t("pos.tables.select_a_table")}
                        </option>
                        {sortTables(tables.value).map((table) => (
                          <option key={table.table.id} value={table.table.id}>
                            {table.table.name}
                            {table.table.is_busy
                              ? ` | ${t("general.words.busy")}`
                              : ""}
                            {table.party ? ` | ${table.party?.name}` : ""}
                            {tableCreditOf(table)?.credited
                              ? ` | ${t("pos.table_credit.title")} ${formatPrice(
                                  tableCreditAvailable(tableCreditOf(table)),
                                  true,
                                  2,
                                  currency.value,
                                )}`
                              : ""}
                          </option>
                        ))}
                      </select>
                      {selectedCredit.value ? null : (
                        <button
                          onClick={() => {
                            props.cartAPI.showFoodTypes.value =
                              !props.cartAPI.showFoodTypes.value;
                          }}
                          class={
                            "btn gap-1 " +
                            ` ${props.cartAPI.showFoodTypes.value ? "btn-accent" : "btn-neutral"}`
                          }
                        >
                          <span class="material-symbols-outlined text-1xl">
                            schedule
                          </span>
                          <span>{t("pos.pay.suite")}</span>
                        </button>
                      )}
                    </div>

                    {selectedCredit.value ? null : (
                      <div class=" space-y-1">
                        <div class="flex items-center gap-2">
                          <span class="material-symbols-outlined text-base text-base-content/55">
                            group
                          </span>
                          <span class="text-xs font-semibold uppercase tracking-wider text-base-content/55">
                            Seats
                          </span>
                        </div>
                        <FastQuantitySelector
                          value={seatCount.value}
                          onChange={handleSeatCountChange}
                          disabled={false}
                          showInModal={true}
                        />
                      </div>
                    )}
                    {cartSellsCredit.value ? (
                      <div
                        role="alert"
                        class="alert alert-warning py-2 text-sm"
                      >
                        <span class="material-symbols-outlined">info</span>
                        <span>{t("pos.table_credit.product_on_table")}</span>
                      </div>
                    ) : null}
                    {cartSellsGiftCard.value ? (
                      <div
                        role="alert"
                        class="alert alert-warning py-2 text-sm"
                      >
                        <span class="material-symbols-outlined">info</span>
                        <span>{t("pos.gift_card.product_on_table")}</span>
                      </div>
                    ) : null}
                    {selectedCredit.value ? (
                      <TableCreditBar
                        credit={selectedCredit.value}
                        owed={selectedCredit.value.owed}
                        orderTotal={cartTotal.value}
                        currency={currency.value}
                        onShowHistory={() => (showCreditHistory.value = true)}
                        allowBeyondCredit={allowBeyondCredit.value}
                      />
                    ) : null}
                    {selectedCredit.value ? null : (
                      <div class="min-h-0 w-full flex flex-col flex-1 overflow-hidden">
                        {props.cartAPI.showFoodTypes.value ? (
                          <CoursesFinalizer
                            cart={props.cart}
                            products={props.products}
                            foodTypes={props.foodTypes}
                            triggeredFoodTypeIds={triggeredFoodTypeIds}
                          />
                        ) : (
                          <TableOrdersSummary
                            tableStatus={selectedTableStatus.value}
                            shopOrders={shop.orders.value}
                            currency={currency.value}
                            onOrderClick={(id, name) => {
                              openOrder.value = { id, name };
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div class="shrink-0 border-t bg-base-100 p-2 lg:p-4">
            <div class="flex justify-between gap-2">
              {props.showCancel ? (
                <button
                  onClick={() => props.onClose()}
                  class="btn btn-neutral"
                  type="button"
                >
                  {t("general.words.cancel")}
                </button>
              ) : null}
              <div class={["flex gap-2", { "flex-1": !props.showCancel }]}>
                {selectedCredit.value ? (
                  <button
                    type="button"
                    class="btn btn-neutral gap-1"
                    disabled={creating.value}
                    onClick={() => (showCreditTopUp.value = true)}
                  >
                    <span class="material-symbols-outlined text-lg">
                      add_card
                    </span>
                    {t("pos.table_credit.add_credit")}
                  </button>
                ) : null}
                <button
                  class={["btn btn-primary", { "flex-1": !props.showCancel }]}
                  type="submit"
                  disabled={
                    !selectedTableId.value ||
                    creating.value ||
                    cartSellsCredit.value ||
                    cartSellsGiftCard.value ||
                    blockedByCredit.value
                  }
                  onClick={handleSubmit}
                >
                  {creating.value ? (
                    <span class="flex items-center justify-center">
                      <span class="loading loading-spinner loading mr-2"></span>
                    </span>
                  ) : (
                    <span>
                      {selectedCredit.value
                        ? t("pos.table_credit.send")
                        : t("general.words.confirm")}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
          {showCreditTopUp.value && selectedTableId.value ? (
            <TableCreditTopUp
              tableId={selectedTableId.value}
              tableName={selectedTableStatus.value?.table.name}
              suggestedAmount={creditShort.value}
              products={props.products}
              onClose={() => (showCreditTopUp.value = false)}
              onDone={async () => {
                // Topped up from here: send the order to the table and close.
                await loadTables();
                if (
                  props.cart.items.length > 0 &&
                  !cartSellsCredit.value &&
                  !cartSellsGiftCard.value
                ) {
                  await handleSubmit();
                }
              }}
            />
          ) : null}
          {showCreditHistory.value &&
          selectedTableId.value &&
          selectedCredit.value ? (
            <TableCreditSheet
              tableId={selectedTableId.value}
              tableName={selectedTableStatus.value?.table.name}
              credit={selectedCredit.value}
              currency={currency.value}
              onClose={() => (showCreditHistory.value = false)}
              onAddCredit={() => {
                showCreditHistory.value = false;
                showCreditTopUp.value = true;
              }}
            />
          ) : null}
          {openOrder.value && selectedTableId.value ? (
            <LemmonModal
              title={`${t("general.words.order")} ${openOrder.value.name}`}
              onClose={() => {
                openOrder.value = null;
              }}
            >
              <div class="flex min-h-0 flex-1 flex-col p-2 lg:p-4">
                <TableOrders
                  tableId={selectedTableId.value}
                  orderId={openOrder.value.id}
                  reservationId={
                    selectedTableStatus.value?.party?.reservation_id ?? null
                  }
                />
              </div>
            </LemmonModal>
          ) : null}
        </div>
      );
    },
  });

export const TableSelectModal = defineComponent<TableSelectModalProps>({
  name: "TableSelectModal",
  props: [
    "isOpen",
    "onClose",
    "onSuccess",
    "cart",
    "cartAPI",
    "selectedTable",
    "foodTypes",
    "products",
  ],
  setup(props) {
    const { t } = useLocalization();
    const isDesktop = ref(
      typeof window !== "undefined" && window.innerWidth > 768,
    );

    const updateViewport = () => {
      isDesktop.value = window.innerWidth > 768;
    };

    onMounted(() => {
      updateViewport();
      window.addEventListener("resize", updateViewport);
    });
    onBeforeUnmount(() => window.removeEventListener("resize", updateViewport));

    const renderInner = (showCancel: boolean) => (
      <TableSelectModalInner {...props} showCancel={showCancel} />
    );

    return () => {
      if (!props.isOpen) return null;

      if (isDesktop.value) {
        return (
          <LemmonModal
            title={t("pos.pay.select_table")}
            onClose={props.onClose}
            disableBottomSheet={true}
            style={
              props.cartAPI.showFoodTypes.value
                ? "height: calc(100dvh - 80px);"
                : ""
            }
          >
            {renderInner(true)}
          </LemmonModal>
        );
      }

      return (
        <FullScreenSheet isOpen={props.isOpen} onClose={() => props.onClose()}>
          <div
            class="flex h-full min-h-0 flex-col bg-base-100"
            style={{ paddingTop: "var(--safe-area-top, 0px)" }}
          >
            <header class="grid shrink-0 grid-cols-[2.5rem_1fr_2.5rem] items-center border-b px-4 py-3">
              <button
                class="btn btn-circle btn-ghost btn-sm"
                type="button"
                aria-label={t("general.words.back")}
                onClick={() => props.onClose()}
              >
                <span class="material-symbols-outlined">arrow_back</span>
              </button>
              <h2 class="text-center text-lg font-semibold text-accent">
                {t("pos.pay.select_table")}
              </h2>
              <span aria-hidden="true" />
            </header>
            {renderInner(false)}
          </div>
        </FullScreenSheet>
      );
    };
  },
});
